import React, { useEffect, useState } from "react";
import "../../App.css";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { LogIn } from "./Schema";
import { useFormik } from "formik";
import { lightAuthTheme, darkAuthTheme } from "../../Theme/authTheme";
import { ThemeProvider } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { getUser } from "../../Redux/Actions/UserActions";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Button, InputAdornment, IconButton, Paper } from "@mui/material";
import { toast } from "react-toastify";
import AuthLogo from "./AuthLogo";
import { useTheme } from "../../contexts/ThemeContext";
import { signInNew, signInLegacy } from "../../utils/cognitoAuth";
import UsernameMigration from "../UsernameMigration";
import { getUserDetails } from "../../utils/srpLogin";

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showMigration, setShowMigration] = useState(false);

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  // Reset showMigration to false when on login page without migration data
  useEffect(() => {
    const migrationData = localStorage.getItem("migrationData");
    if (
      !migrationData &&
      (location.pathname === "/login" || location.pathname === "/")
    ) {
      setShowMigration(false);
    }
  }, [location.pathname]);

  const handleSignUpClick = () => {
    navigate("/signup");
  };

  const handleForgotPassword = () => {
    navigate("/forgot");
  };

  const initialValues = {
    username: "",
    password: "",
    keepLoggedIn: false,
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: LogIn,
      onSubmit: async ({ username, password, keepLoggedIn }) => {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Authenticating..."));

        try {
          // First try to authenticate with the new Cognito pool
          const result = await signInNew(username, password);

        
            const userDetails = await getUserDetails({
              region: process.env.REACT_APP_AWS_REGION,
              accessToken: result.accessToken,
            });
          
            
            // Store JWT tokens for socket authentication
            const userData = {
              username: userDetails.attributes.email,
              idToken: result.idToken,
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              keepLoggedIn,
              tokenRefreshedAt: Date.now(), // Track when tokens were created
              firstName: userDetails.attributes.given_name|| '',
              lastName: userDetails.attributes.family_name || '',
            };
            dispatch(getUser(userData));
        
        } catch (newPoolError) {
          try {
            // If new pool fails, try legacy pool for migration
            const legacyResult = await signInLegacy(username, password);

            // Store migration data temporarily with all legacy tokens
            const migrationData = {
              username: username,
              password: password,
              keepLoggedIn,
              idToken: legacyResult.idToken,
              accessToken: legacyResult.accessToken,
              refreshToken: legacyResult.refreshToken,
            };

            localStorage.setItem(
              "migrationData",
              JSON.stringify(migrationData)
            );

            dispatch(updateLoading(false));

            // Show migration component
            setShowMigration(true);
          } catch (legacyError) {
            dispatch(updateLoading(false));
            console.error(
              "Both authentication methods failed:",
              newPoolError,
              legacyError
            );

            let errorMessage = "Incorrect username or password";
            if (legacyError.code === "UserNotConfirmedException") {
              errorMessage =
                "User account is not confirmed. Please check your email.";
            } else if (legacyError.code === "PasswordResetRequiredException") {
              errorMessage =
                "Password reset required. Please reset your password.";
            } else if (legacyError.code === "UserNotFoundException") {
              errorMessage = "User not found. Please check your email.";
            } else if (legacyError.message) {
              errorMessage = legacyError.message;
            }

            toast.error(errorMessage);
          }
        }
      },
    });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to handle hiding migration component
  const handleHideMigration = () => {
    setShowMigration(false);
  };

  // If migration is required, show migration component
  if (showMigration) {
    return <UsernameMigration onHideMigration={handleHideMigration} />;
  }

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          zoom: 0.75,
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow:
                authTheme.palette.mode === "dark"
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <AuthLogo />
            </Box>

            {/* Welcome Text */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 1,
                }}
              >
                Welcome Back!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                Let's get you signed in securely.
              </Typography>
            </Box>

            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 3 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            >
              {/* Email Field */}
              <TextField
                fullWidth
                variant="outlined"
                id="username"
                name="username"
                label="Email"
                placeholder="Enter Your Email Address"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
                sx={{ mb: 3 }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                variant="outlined"
                id="password"
                name="password"
                label="Password"
                placeholder="Your Password"
                type={showPassword ? "text" : "password"}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ color: authTheme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      id="keepLoggedIn"
                      name="keepLoggedIn"
                      checked={values.keepLoggedIn}
                      onChange={handleChange}
                      sx={{
                        color: authTheme.palette.text.secondary,
                        "&.Mui-checked": {
                          color: authTheme.palette.secondary.main,
                        },
                      }}
                    />
                  }
                  label="Keep Me Logged In"
                  sx={{
                    m: 0,
                    color: authTheme.palette.text.secondary,
                  }}
                />

                <Link
                  component="button"
                  variant="body2"
                  onClick={handleForgotPassword}
                  sx={{
                    color: authTheme.palette.secondary.main,
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot Your Password?
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor:
                    authTheme.palette.mode === "dark" ? "#F9FAFB" : "#1F2937",
                  color:
                    authTheme.palette.mode === "dark" ? "#ffffff" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor:
                      authTheme.palette.mode === "dark" ? "#E5E7EB" : "#374151",
                  },
                }}
              >
                Log into your account
              </Button>

              {/* Sign Up Link */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ color: authTheme.palette.text.secondary }}
                >
                  Don't Have an Account?{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleSignUpClick}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default SignIn;
